import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, Check, X, Lock, IdCard, CreditCard, RotateCcw, Percent, Landmark, Rocket, ArrowLeftRight } from "lucide-react";
import { RevenuePanel, PayoutPanel, ProductionPanel } from "@/components/AdminProduction";
import { TransfersPanel } from "@/components/AdminTransfers";
import { initials, timeAgo, euro, dateTime } from "@/lib/format";
import { toast } from "sonner";

const KYC_TABS = [
  { key: "pending", label: "En attente" },
  { key: "approved", label: "Vérifiés" },
  { key: "rejected", label: "Refusés" },
];

const KIND_LABELS = {
  wallet_topup: "Recharge",
  photo_purchase: "Achat photo",
  subscription: "Abonnement créateur",
  app_subscription: "Abonnement app",
};

const STATUS_STYLES = {
  paid: "bg-[hsl(160_84%_32%)] text-white border-0",
  pending: "bg-[hsla(38,92%,50%,0.15)] text-[hsl(38_92%_38%)] border-0",
  failed: "bg-destructive/15 text-destructive border-0",
  refunded: "bg-secondary text-muted-foreground border-0",
  expired: "bg-secondary text-muted-foreground border-0",
};

const PaymentsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [kindFilter, setKindFilter] = useState("all");
  const [refundTarget, setRefundTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/payments?pay_status=${filter}&kind=${kindFilter}`);
      setRows(data.payments || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter, kindFilter]);

  useEffect(() => { load(); }, [load]);

  const doRefund = async () => {
    if (!refundTarget) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/payments/${refundTarget.session_id}/refund`, {});
      toast.success(`Remboursement de ${euro(data.amount)} effectué`);
      setRefundTarget(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">Toutes les transactions Stripe et leur statut.</p>
        <div className="flex items-center gap-2">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger className="w-40 rounded-xl" data-testid="admin-payments-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les types</SelectItem>
              <SelectItem value="wallet_topup">Recharge</SelectItem>
              <SelectItem value="photo_purchase">Achat photo</SelectItem>
              <SelectItem value="subscription">Abonnement créateur</SelectItem>
              <SelectItem value="app_subscription">Abonnement app</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-36 rounded-xl" data-testid="admin-payments-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="paid">Payés</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="failed">Échoués</SelectItem>
              <SelectItem value="refunded">Remboursés</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><CreditCard className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">Aucune transaction</p>
        </div>
      ) : (
        <Card className="rounded-2xl overflow-hidden" data-testid="admin-payments-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Montant</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const st = r.payment_status || r.status;
                const canRefund = st === "paid" && !r.refunded;
                return (
                  <TableRow key={r.session_id} data-testid={`admin-payment-${r.session_id}`}>
                    <TableCell className="max-w-[140px]">
                      <p className="font-medium truncate text-sm">@{r.user?.username || "—"}</p>
                      <p className="text-[11px] text-muted-foreground truncate">{r.user?.email || ""}</p>
                    </TableCell>
                    <TableCell className="text-sm">{KIND_LABELS[r.kind] || r.kind || "Paiement"}</TableCell>
                    <TableCell className="text-right font-medium">{euro((r.amount || 0) / 100)}</TableCell>
                    <TableCell>
                      <Badge className={`capitalize ${STATUS_STYLES[st] || "bg-secondary text-muted-foreground border-0"}`}>{st}</Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{dateTime(r.created_at)}</TableCell>
                    <TableCell className="text-right">
                      {canRefund ? (
                        <Button size="sm" variant="outline" className="rounded-lg text-destructive border-destructive/30 hover:bg-destructive/5"
                          onClick={() => setRefundTarget(r)} data-testid={`admin-refund-${r.session_id}`}>
                          <RotateCcw className="h-3.5 w-3.5 mr-1" /> Rembourser
                        </Button>
                      ) : r.refunded ? (
                        <span className="text-xs text-muted-foreground">Remboursé</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <AlertDialog open={!!refundTarget} onOpenChange={(v) => !v && setRefundTarget(null)}>
        <AlertDialogContent data-testid="admin-refund-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer le remboursement</AlertDialogTitle>
            <AlertDialogDescription>
              Rembourser <strong>{euro((refundTarget?.amount || 0) / 100)}</strong> à @{refundTarget?.user?.username} via Stripe ?
              Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doRefund} disabled={busy} className="bg-destructive hover:bg-destructive/90" data-testid="admin-refund-confirm">
              {busy ? "Remboursement…" : "Rembourser"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const KycPanel = () => {
  const [tab, setTab] = useState("pending");
  const [users, setUsers] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/kyc?state=${tab}`);
      setUsers(data.users);
      setCounts(data.counts);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision) => {
    try {
      await api.post(`/admin/kyc/${id}/${decision}`);
      toast.success(decision === "approve" ? "Identité vérifiée" : "Pièce refusée");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex rounded-xl bg-secondary p-1" data-testid="admin-kyc-tabs">
        {KYC_TABS.map((tt) => (
          <button key={tt.key} onClick={() => setTab(tt.key)} data-testid={`admin-tab-${tt.key}`}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${tab === tt.key ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
            {tt.label} <span className="text-xs opacity-70">({counts[tt.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}</div>
      ) : users.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><IdCard className="h-6 w-6" /></div>
          <p className="font-medium text-foreground">Aucun dossier {KYC_TABS.find((x) => x.key === tab)?.label.toLowerCase()}</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="admin-kyc-list">
          {users.map((u) => (
            <Card key={u.id} className="p-4 rounded-2xl" data-testid={`admin-kyc-${u.id}`}>
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9"><AvatarFallback className="bg-accent text-accent-foreground text-xs font-semibold">{initials(u.username)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">@{u.username}</p>
                  <p className="text-xs text-muted-foreground truncate">{u.email} · {timeAgo(u.created_at)}</p>
                </div>
                <Badge variant="secondary" className="capitalize">{u.kyc_status}</Badge>
              </div>
              {u.kyc_id_image ? (
                <img src={u.kyc_id_image} alt="pièce d'identité" className="w-full max-h-64 object-contain rounded-xl border border-border bg-secondary" data-testid={`admin-kyc-image-${u.id}`} />
              ) : (
                <p className="text-xs text-muted-foreground">Aucune image fournie.</p>
              )}
              <div className="flex gap-2 mt-3">
                <Button size="sm" className="flex-1 rounded-xl bg-[hsl(160_84%_32%)] hover:bg-[hsl(160_84%_28%)] text-white" onClick={() => decide(u.id, "approve")} data-testid={`admin-approve-${u.id}`}>
                  <Check className="h-4 w-4 mr-1" /> Valider
                </Button>
                <Button size="sm" variant="outline" className="flex-1 rounded-xl text-destructive border-destructive/30 hover:bg-destructive/5" onClick={() => decide(u.id, "reject")} data-testid={`admin-reject-${u.id}`}>
                  <X className="h-4 w-4 mr-1" /> Refuser
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

const SECTIONS = [
  { key: "payments", label: "Paiements", icon: CreditCard },
  { key: "revenue", label: "Revenus", icon: Percent },
  { key: "payouts", label: "Virements", icon: Landmark },
  { key: "transfers", label: "Historique", icon: ArrowLeftRight },
  { key: "production", label: "Production", icon: Rocket },
  { key: "kyc", label: "KYC", icon: IdCard },
];

const Admin = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initial = SECTIONS.some((s) => s.key === searchParams.get("section")) ? searchParams.get("section") : "payments";
  const [section, setSectionState] = useState(initial);
  const setSection = (key) => {
    setSectionState(key);
    setSearchParams(key === "payments" ? {} : { section: key }, { replace: true });
  };

  if (user && !user.is_admin) {
    return (
      <div className="text-center py-20">
        <div className="mx-auto h-14 w-14 rounded-2xl bg-secondary grid place-items-center mb-3"><Lock className="h-6 w-6 text-muted-foreground" /></div>
        <p className="font-display text-lg">Accès réservé aux administrateurs</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/")}>Retour</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-2xl flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-primary" /> Administration</h1>
        <p className="text-sm text-muted-foreground mt-1">Paiements, commission 20 %, virements vers votre banque et passage en production.</p>
      </div>

      <div className="flex flex-wrap rounded-xl bg-secondary p-1 gap-1" data-testid="admin-section-tabs">
        {SECTIONS.map((s) => (
          <button key={s.key} onClick={() => setSection(s.key)} data-testid={`admin-section-${s.key}`}
            className={`flex-1 min-w-[120px] py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1.5 ${section === s.key ? "bg-card shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </button>
        ))}
      </div>

      {section === "payments" && <PaymentsPanel />}
      {section === "revenue" && <RevenuePanel />}
      {section === "payouts" && <PayoutPanel />}
      {section === "transfers" && <TransfersPanel />}
      {section === "production" && <ProductionPanel />}
      {section === "kyc" && <KycPanel />}
    </div>
  );
};

export default Admin;
